import { Button, Checkbox, FormControl, FormHelperText, Grid, IconButton, InputLabel, MenuItem, Paper, Popper, Select, TextField, Typography, createFilterOptions } from "@mui/material";
import { blue, red } from "@mui/material/colors";
import { ColourChip } from "../../../components/general/mini_components-ts";
import { getBillableCategories, filterBillablesByCategory, isFreeItemInProgress, isValidFreeItemType, isBillItemType, FREE_ITEM_TYPE_OPTIONS } from "./billItemAddHelpers";
import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LocalizeContextProps, Translate, withLocalize } from "react-localize-redux";
import { useHistory } from "react-router-dom";
import { ButtonAdd, ButtonCancel, ButtonContinue, ButtonContinueStyles, ButtonOk, IconGenerator } from "../../../components/general/mini_components";
import { TYPES_DISCOUNT, TYPE_BILL_ITEM } from "../../../constants/types";
import { getBillItemTypeKey, getBillItemTypeOptions, orderBillItemTypeOptionsByLabel } from "../../../utils/billItemTypeOptions";
import { calculateTotalBill, getDateFromStringOrDate } from "../../../utils/bill";
import { validateBillItemField } from "../../../utils/billItemValidation";
import { getNextOrderNumber as getNextOrderNumberUtil } from "../../../utils/billCalculations";
import { Bill, Billable, BillItem, BillItemKeys, BillItemModes, BillItemTable, DocumentStatus, DocumentType, PaymentMethod, CreatePaymentRequest } from "./types";
import { isBillCancelled } from "./billDocumentAccess";
import styled from "styled-components"
import { Autocomplete } from "@mui/lab"
import { EnhancedTable } from "../../../components/general/EnhancedTable";
import _, { set } from "lodash";
import { dateToFullDateString, hasDefaultValues } from "../../../utils/index.jsx";
import { useDispatch, useSelector } from "react-redux";
import { pushBillingItems, saveBillingItems } from "../../../redux/actions/billingActions";
import Modal from "../../../components/general/modal";
import FillSurvey from "../FillSurvey";
import ShowSingleSubmissionPatient from "../ShowSingleSubmissionPatient";
import QuantitySelector from "./QuantitySelector";
import PaymentMethodModal from "./PaymentMethodModal";
import { removeSubmissionPatient } from "../../../redux/actions/submissionsPatientActions";
import { getSubmissionPatientService } from "../../../services";
import { HOSPITAL_BILLING_VIEW_DOCUMENT } from "../../../routes/urls";
import { refreshBillPricesService } from "../../../services/billing";
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';

const FactureHolder = styled.div<{ hide: boolean }>`
    margin-top:2rem;
    text-align:center;
    background-color:#e6e6e6;
    padding:0.5rem;
    color:#000;
    font-weight:bold;
    display:${props => props.hide ? 'none' : 'auto'};
`;

const GridBottom = styled(Grid) <{ hide: boolean }>`
    display:${props => props.hide ? 'none' : 'flex'};
    justify-content:end;
`;

export interface Column{
    name:keyof BillItem,
    type:string,
    validation:string,
    markAllCallback?:() => Promise<void>,
    translationKey?:string // Optional custom translation key. If not provided, uses hospital.billing.item.${name}
}

export interface BillItemsProps extends LocalizeContextProps{
    columns:Column[]
    currency : string,
    print:boolean,
    mode : BillItemModes,
    bill: Bill | null,
    initItems:BillItem[],
    showTotal:boolean,
    repeatBillItems:boolean,
    canUpdateBill:boolean,
    canEditPharmacyPrices:boolean,
    hidePatientPaymentUi: boolean,
    uuidInvestigation?:string,
    error:ReactElement | undefined,
    withDiscount:boolean,
    uuidPatient?:string,
    surveyAdditionalInfo:any,
    canUseAdditionalInfo:boolean,
    onBillItemsValidated : (items: BillItem[]) => void,
    onUpdateBillItemStatus:(items: BillItem[], paymentRecord?: CreatePaymentRequest) => void,
    onCancelBill: () => void,
    onPaymentSuccess?: () => void;
    hideAddItemFooter?: boolean;
    // SHER-460: when the catalogue is owned by the PrestationSelector add bar, the "+"
    // add-row is kept ONLY for off-catalogue free items. In that mode it loads no
    // catalogue billables and shows no category pills, so the two add paths don't
    // intersect — you type a concept and pick a curated free-item Type.
    addRowFreeItemOnly?: boolean;
    // SHER-489: when false, selecting a catalogue product no longer auto-adds the
    // row (the SHER-460 "keep-open batch add" for billing cashiers). Instead the
    // user types the quantity and clicks "+" to add. The pharmacy request
    // (RequestForm) sets this false so large quantities aren't incremented from 1
    // one-by-one; billing keeps the default (auto-add) behavior.
    autoAddOnSelect?: boolean;
}

type BillableOption = Billable & {inputValue?: string, isFreeItemOption?: boolean}

const TYPES_BILL_ITEM = getBillItemTypeOptions();

// SHER-460: deterministic palette for the category-filter pills in the add-bill
// autocomplete. Colours are assigned by the category's position in the sorted list
// so the same category keeps the same colour within a session.
const CATEGORY_PILL_COLORS = ["#1976d2", "#388e3c", "#d32f2f", "#f57c00", "#7b1fa2", "#0097a7", "#5d4037", "#c2185b", "#455a64", "#00796b"];
const categoryPillColor = (index: number) => CATEGORY_PILL_COLORS[index % CATEGORY_PILL_COLORS.length];
const billableFilterOptions = createFilterOptions<BillableOption>({ stringify: (option) => option.concept ?? "" });

// SHER-460: by default MUI pins the autocomplete dropdown to the input width, which is too
// narrow for long lab/radiology billable names. This Popper lets the dropdown grow wider than
// the field (sizes to its content, with a floor) so the full concept + category pills fit.
const WideBillablePopper = (props: any) => (
    <Popper {...props} style={{ ...(props.style || {}), width: "auto", minWidth: 480 }} placement="bottom-start" />
);
// Si tiene una columna de tipo "amount" entonces se calcula el total y se valida la suma de todos los amounts sea mayor que 0

const BillItemsCore:React.FC<BillItemsProps> = ({ columns, canUseAdditionalInfo, error, activeLanguage,
                                                    canUpdateBill, canEditPharmacyPrices, currency, print, withDiscount,
                                                    surveyAdditionalInfo, uuidInvestigation,
                                                    uuidPatient, initItems, hidePatientPaymentUi, hideAddItemFooter, addRowFreeItemOnly,
                                                    autoAddOnSelect = true,
                                                    bill, translate, showTotal, repeatBillItems,
                                                    onUpdateBillItemStatus, onBillItemsValidated,
                                                    onCancelBill, onPaymentSuccess }) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const paymentMethods = useSelector((state:any) => state.investigations.currentInvestigation.billingInfo.paymentMethods);
    const [showModal, setShowModal] = useState({show:false, type:""});
    const [showAdditionalInfoID, setShowAdditionalInfoID] = useState(-1);
    const filteredColumns:Column[] = columns.filter((column:Column) => column.type !== "type" || withDiscount)
    // SHER-546: whether the bill's own Type column is actually on screen. Everything that
    // used to test `withDiscount` for this really meant "is there a Type control?", which is
    // this — the flag alone is true even when the caller passed no `type` column at all.
    const hasTypeColumn:boolean = filteredColumns.some((column:Column) => column.type === "type")
    const initFieldErrors:any = filteredColumns.reduce((acc:{[id: string] : string}, column) => {
        acc[column.name] = "";
        return acc;
    }, {});
    const DEFAULT_CURRENT_ITEM = initFieldErrors;
    const [fieldErrors, setFieldErrors] = useState(initFieldErrors);
    const [addingItem, setAddingItem] = useState(false);
    const [refreshingPrices, setRefreshingPrices] = useState(false);
    const [refreshResult, setRefreshResult] = useState<{ show: boolean, count: number } | null>(null);
    // SHER: inline edit of a bill item's unit price. Only one row edits at a time.
    const [editingPriceIndex, setEditingPriceIndex] = useState<number | null>(null);
    const [editingPriceValue, setEditingPriceValue] = useState<string>("");
    
    // Use imported utility function to get the next order number
    const getNextOrderNumber = getNextOrderNumberUtil;
    
    const items:BillItem[]  = useSelector((state:any) => {
        return state.billing.data.billItems ? state.billing.data.billItems.sort((bItemA:BillItem, bItemB:BillItem) => {
            // Primary sort by order if both items have it
            if (bItemA.order !== undefined && bItemB.order !== undefined) {
                return bItemA.order - bItemB.order;
            }
            // Fallback to timestamp-based sorting for items without order
            if(bItemA.updatedAt && bItemB.updatedAt){
                const dateA = bItemA.createdAt ? new Date(bItemA.createdAt).getTime() : 0;
                const dateB = bItemB.createdAt ? new Date(bItemB.createdAt).getTime() : 0;
                
                return canUpdateBill ? dateB - dateA : dateA - dateB;
            }
            return 0;
        }) : []});
    const totalAmountBill = useMemo(() => {

        const removeTypes = print ? [TYPE_BILL_ITEM.DISCOUNT_ADDITIONAL_INFO, TYPE_BILL_ITEM.HIDDEN_VALUE] : [TYPE_BILL_ITEM.DISCOUNT_ADDITIONAL_INFO];
        return calculateTotalBill(items, removeTypes);
    }, [items]);

    const billables:Billable[] = useSelector((state:any) => state.billing.data.billablesCurrentBill ? state.billing.data.billablesCurrentBill : []);
    //const [items, setItems] = useState<BillItem[]>(initItems);
    const [currentItem, setCurrentItem] = useState<BillItem>(DEFAULT_CURRENT_ITEM as BillItem);    
    const [errorBill, setErrorBill] = useState<ReactElement | undefined>(error ? error : undefined);
    const [editSubmission, setEditSubmission] = useState<any | undefined>(undefined);

    const [itemAdded, setItemAdded] = useState(false);
    // SHER-460: active category-filter pill in the add-bill autocomplete (null = no filter)
    // and a remount counter that clears the autocomplete after each catalogue pick so the
    // cashier can chain several items without reopening it (keep-open batch add).
    const [activeCategory, setActiveCategory] = useState<string | null>(null);
    const [addAutocompleteKey, setAddAutocompleteKey] = useState(0);
    const billableCategories = useMemo(() => getBillableCategories(billables), [billables]);
    // SHER-460: one colour per category, shared by the filter pills and the per-option category
    // chips so the same category reads the same colour everywhere in the dropdown.
    const categoryColorMap = useMemo(() => {
        const map = new Map<string, string>();
        billableCategories.forEach((category, index) => map.set(category, categoryPillColor(index)));
        return map;
    }, [billableCategories]);

    const printRef = useRef<HTMLHeadingElement>(null);

    // SHER-460 #7: the add-row Concept autocomplete is remounted (via addAutocompleteKey) after
    // each keep-open catalogue pick so its input clears for the next item. A declarative
    // `autoFocus` re-runs on that remount and the browser scrolls the freshly-focused input into
    // view — since the add-row sits at the bottom of a long bill, the whole page jumps down after
    // every add (Pedro's report). Focus the input imperatively with `preventScroll` instead: the
    // cursor is still ready for the next item, but the page keeps its scroll position. Memoised so
    // the callback ref fires only on real mount/unmount, never re-stealing focus mid-typing.
    const focusConceptWithoutScroll = useCallback((node: HTMLInputElement | null) => {
        if (node) node.focus({ preventScroll: true });
    }, []);

    useEffect(() => {
        console.log("currentItem cambió", currentItem);
        if(itemAdded){
            // SHER-460: a catalogue pick auto-adds the row; keep the add-row + autocomplete
            // open afterwards so the cashier can chain several items (keep-open batch add).
            addCurrentItem(true);
        }
        setItemAdded(false);
        
    }, [currentItem]);

    //Si hay initItems, los cargo en redux
    useEffect(() => {
        if(initItems.length > 0){
            // Assign order to items that don't have one (for backwards compatibility)
            const itemsWithOrder = initItems.sort((a, b) => (a.id || 0) - (b.id || 0)).map((item, index) => ({
                ...item,
                order: item.order !== undefined ? item.order : index + 1
            }));
            dispatch(saveBillingItems(itemsWithOrder));
        }
    }, [initItems]);

    // Auto-fetch amounts for DISCOUNT_ADDITIONAL_INFO items
    useEffect(() => {
        async function fetchAdditionalInfoAmounts() {
            if (!uuidInvestigation) return;
            
            // Find DISCOUNT_ADDITIONAL_INFO items that need amount fetching
            const itemsToFetch = items.filter(item => 
                Number(item.type) === TYPE_BILL_ITEM.DISCOUNT_ADDITIONAL_INFO && 
                item.additionalInfoId && 
                (!item.amount || Number(item.amount) === 0)
            );

            if (itemsToFetch.length === 0) return;

            try {
                const updatedItems = [...items];
                let hasUpdates = false;

                for (const item of itemsToFetch) {
                    const itemIndex = updatedItems.findIndex(i => 
                        i.additionalInfoId === item.additionalInfoId && 
                        Number(i.type) === TYPE_BILL_ITEM.DISCOUNT_ADDITIONAL_INFO
                    );
                    
                    if (itemIndex !== -1) {
                        try {
                            const response = await getSubmissionPatientService(uuidInvestigation, item.additionalInfoId, false);
                            if (response.submission && response.submission.surveyRecords) {
                                const amountField = response.submission.surveyRecords.find((record: any) => 
                                    record.surveyField.name.toLowerCase() === "amount"
                                );
                                
                                if (amountField && amountField.value) {
                                    updatedItems[itemIndex] = {
                                        ...updatedItems[itemIndex],
                                        amount: Number(amountField.value),
                                        unitCost: Number(amountField.value),
                                        updatedAt: new Date()
                                    };
                                    hasUpdates = true;
                                }
                            }
                        } catch (error) {
                            console.error(`Failed to fetch amount for additionalInfoId ${item.additionalInfoId}:`, error);
                        }
                    }
                }

                if (hasUpdates) {
                    await dispatch(saveBillingItems(updatedItems));
                }
            } catch (error) {
                console.error('Error fetching additional info amounts:', error);
            }
        }

        fetchAdditionalInfoAmounts();
    }, [items, uuidInvestigation, dispatch]);

    function updateStates(itemKey: BillItemKeys, error: string, value: string) {
        let tempFieldErrors = { ...fieldErrors };
        tempFieldErrors[itemKey] = error;
        let tempCurrentItem:BillItem = { ...currentItem };

        const col = filteredColumns.find((col) => {
            return col.name === itemKey
        })
        if(col){
            const key = itemKey.toString() as keyof BillItem;
            const valueState = col.type === "number" ? Number(value) : value;
            if(tempCurrentItem.hasOwnProperty(key)){
                // @ts-ignore: Unreachable code error
                tempCurrentItem[key] = valueState as string;
            }
        }
        
        setFieldErrors(tempFieldErrors);
        setCurrentItem(tempCurrentItem);
    }

    async function toggleAllItemsAs(category:"paid" | "used"){
        const tempItems = [...items];
        tempItems.forEach((item) => {
            const itemValue = item[category];
            // Handle boolean (backward compatibility)
            if (typeof itemValue === "boolean") {
                item[category] = !itemValue as any;
            }
            // Handle Date | number | null (new format)
            else if (itemValue === null || itemValue === undefined) {
                item[category] = true as any;
            } else {
                // If it's a Date or number, clear it
                item[category] = null as any;
            }
        });
        await dispatch(saveBillingItems(tempItems));
    }

    function getAmount(val:BillItem){
        return print && val.amountAlter ? val.amountAlter : val.amount;
    }
    function updateQuantityBillItem(index:number, quantity:number){
        const tempItems = [...items];
        const error = validateKeyItem(quantity.toString(), "quantity");
        if(error === ""){
            tempItems[index].quantity = quantity;
            dispatch(saveBillingItems(tempItems));
        }
    }
    function updateUnitCostBillItem(index:number, unitCost:number){
        if(unitCost > 0){
            const tempItems = [...items];
            tempItems[index] = { ...tempItems[index], amount: unitCost };
            dispatch(saveBillingItems(tempItems));
        }
    }
    // SHER: a row's unit price is editable for normal priced lines (same trust level as
    // editing the quantity). Discounts, percent/derived lines and pharmacy items keep
    // their own pricing rules and are excluded; pharmacy uses canEditPharmacyPrices.
    function isPriceEditable(item:BillItem){
        const type = Number(item.type);
        return canUpdateBill &&
            !TYPES_DISCOUNT.includes(type) &&
            type !== TYPE_BILL_ITEM.AUTO_ANAESTHESIA &&
            type !== TYPE_BILL_ITEM.PERCENT_CHARGE &&
            type !== TYPE_BILL_ITEM.PHARMACY_ITEM &&
            type !== TYPE_BILL_ITEM.HIDDEN_VALUE &&
            item.type !== TYPE_BILL_ITEM.DISCOUNT_ADDITIONAL_INFO;
    }
    function commitPrice(index:number){
        // Enter then the resulting blur both fire commit; once we've left edit mode the
        // second call is a no-op (guards against a stale re-parse). updateUnitCostBillItem
        // owns the >0 floor.
        if(editingPriceIndex !== index) return;
        const parsed = parseFloat(editingPriceValue);
        if(!isNaN(parsed)){
            updateUnitCostBillItem(index, parsed);
        }
        setEditingPriceIndex(null);
    }

    function validateKeyItem(value: string, itemKey: string) {
        const col = filteredColumns.find((col) => col.name === itemKey);
        if (!col) return "";
        return validateBillItemField(value, itemKey, col, currentItem, billables);
    }
    function changeType(event: React.ChangeEvent<{
        name?: string | undefined;
        value: string;
    }>, itemKey: BillItemKeys) {
        const valueString = event.target.value.toString() as string;
        const error = validateKeyItem(valueString, itemKey);
        updateStates(itemKey, error, valueString);
        if(valueString === TYPE_BILL_ITEM.DISCOUNT_ADDITIONAL_INFO.toString()){
            console.log("show nomdal");
            setShowModal({show:true, type:"additional_info"});
        }
    }
    function changeField(value:string, itemKey: BillItemKeys) {
        const error = validateKeyItem(value, itemKey);
        updateStates(itemKey, error, value);
    }
    // SHER-460: set a free item's type directly. `updateStates` only writes a key when a matching
    // column exists in `filteredColumns`; for a free item on a no-discount bill there is no Type
    // column, so we update currentItem.type ourselves and clear/raise the required-type error.
    function changeFreeItemType(value: string) {
        setCurrentItem((prev) => ({ ...prev, type: Number(value) }));
        setFieldErrors((prev:any) => ({ ...prev, type: isValidFreeItemType(value) ? "" : "type_not_selected" }));
    }
    function onBillableSelected(billable:string, billableId?:number, index?:number){
        console.log(billable);
        console.log(billableId); 
        if(!billableId){
            updateStates(BillItemKeys.concept, "", billable);
        }
        else{
            const billableSelected = billables?.find((billable) => billable.id === billableId);
            if(billableSelected && index !== undefined){
                // changeField(billableSelected.concept, BillItemKeys.concept);
                // changeField(billableSelected.amount.toString(), BillItemKeys.amount);
                // changeField(billableSelected.type.toString(), BillItemKeys.type);
                const selectedQuantity = Number(currentItem.quantity);
                const updatedItem:BillItem = {
                    ...currentItem,
                    ...billableSelected,
                    billableId:billableSelected.id,
                    unitCost: billableSelected.amount,
                    quantity: selectedQuantity > 0 ? selectedQuantity : 1,
                    updatedAt: new Date()
                };
                delete updatedItem.id;
                setCurrentItem(updatedItem);
                
                if(autoAddOnSelect && !hasDefaultValues(updatedItem, DEFAULT_CURRENT_ITEM)){
                    setItemAdded(true);
                }
                
            }
        }
    }
    function helperText(itemType: BillItemKeys): null | ReactElement {
        return fieldErrors[itemType] ? <Translate id={`hospital.billing.item.error.${fieldErrors[itemType]}`} /> : null;
    }
    
    async function onRefreshPrices() {
        if (!bill?.uuid || !uuidInvestigation) return;
        setRefreshingPrices(true);
        try {
            const response = await refreshBillPricesService(uuidInvestigation, bill.uuid);
            if (response.status === 200 && response.bill) {
                dispatch(saveBillingItems(response.bill.billItems));
                setRefreshResult({ show: true, count: response.updatedItems.length });
                setTimeout(() => setRefreshResult(null), 3000);
            }
        } catch (error) {
            console.error("Error refreshing prices", error);
            setRefreshResult({ show: true, count: -1 });
            setTimeout(() => setRefreshResult(null), 5000);
        } finally {
            setRefreshingPrices(false);
        }
    }

    async function onClickContinue(items: BillItem[]) {
        
        if(filteredColumns.find((col) => col.type === BillItemKeys.amount)){
            if (totalAmountBill < 0) {
                setErrorBill(<Translate id="hospital.billing.billing_info.error.positive" />);
                return;    
            }
        }
        if(bill && bill.status === DocumentStatus.CLOSED){
            // Check if any items are paid (Date or number means paid, null/undefined means not paid)
            const isPayingItems = items.some((item) => {
                return item.paid !== null && item.paid !== undefined;
            });
            if (isPayingItems) {
                setShowModal({show:true, type:"payment_method"});
                return;
            }
        }
        else{
            onBillItemsValidated(items);
        }
        setAddingItem(false);
    }
    
    async function usedItem(index: number) {
        console.log(index);
        const tempItems = [...items] as BillItem[];
        const currentValue = tempItems[index].used;
        
        // Toggle: null/undefined -> current date, Date/number -> null
        if (currentValue === null || currentValue === undefined) {
            tempItems[index].used = new Date();
        } else {
            tempItems[index].used = null;
        }
        
        await dispatch(saveBillingItems(tempItems));
    }
    async function paidItem(index: number) {
        console.log(index);
        const tempItems = [...items] as BillItem[];
        const currentValue = tempItems[index].paid;
        
        // Toggle: null/undefined -> current date, Date/number -> null
        if (currentValue === null || currentValue === undefined) {
            tempItems[index].paid = true;
        } else {
            tempItems[index].paid = null;
        }
        
        await dispatch(saveBillingItems(tempItems));
    }
   
    function renderTextOkButton() {
        if (bill && isBillCancelled(bill.status)) {
            return null;
        }
        if (!canUpdateBill) {
            if(bill && bill.status === DocumentStatus.CLOSED && bill.type === DocumentType.INVOICE){
                return <Translate id="hospital.billing.bill.pay" />
            }
            return <Translate id="hospital.billing.bill.update" />
        }
        else {
            return <Translate id="hospital.billing.bill.save" />
        }
    }

    function renderCheckOrDate(color: "default" | "primary" | "secondary" | undefined,
        value: boolean | string | undefined, index: number, fn: (index: number) => void) {
        if (typeof value == "boolean" || value === null) {
            if(print){
                return <Translate id="general.no" />
            }
            else{
                return <Checkbox color={color} checked={value} onClick={() => fn(index)} />
            }
        }
        else if (value) {
            // BUG B fix: `value` can be a Date, a string, OR a number (ms-since-epoch)
            // depending on what upstream put into the record. Unconditional `.replace()`
            // crashed with "nr.replace is not a function" when a ms-epoch number reached
            // this branch (observed on chom-dakar /billing/view/b072a069 today, 7 hits
            // in 75 minutes). Normalize to Date defensively.
            let dateObject: Date;
            if (typeof value === 'string') {
                dateObject = new Date(value.replace(' ', 'T').replace(' ', 'Z'));
            } else {
                dateObject = new Date(value); // handles Date and ms-number
            }
            if (dateObject && !isNaN(dateObject.getTime())) {
                return <>
                    {
                        dateObject.getDate() + " " + dateObject.toLocaleString(activeLanguage.code, { month: 'short', year: 'numeric' })
                    }
                </>
            }
        }
    }

    function renderContentModal(){
        if(showModal.type === "payment_method"){
            return <PaymentMethodModal paymentMethods={paymentMethods} 
                activeLanguage={activeLanguage}
                onClose={() => setShowModal({show:false, type:""})}
                onSelectPaymentMethod={(paymentRecord: { amount: number, paymentMethodInvestigationId: number, paymentData?: any }) => {
                    console.log('[BillItems] Payment method selected:', paymentRecord);
                    console.log('[BillItems] Items to update:', items.map(i => ({ concept: i.concept, paid: i.paid })));
                    dispatch(saveBillingItems(items));
                    setShowModal({show:false, type:""});
                    console.log('[BillItems] Calling onUpdateBillItemStatus...');
                    onUpdateBillItemStatus(items, paymentRecord);
                    if (onPaymentSuccess) onPaymentSuccess();
                }}
            />
        }
        else if(showModal.type === "additional_info"){
            if(showAdditionalInfoID !== -1 && !editSubmission){
                return <ShowSingleSubmissionPatient currentSurvey={surveyAdditionalInfo} forceEdit={false}
                            idSubmission={showAdditionalInfoID} 
                            callBackEditSubmission={(idSubmission:number, uuidSection:string, submission:any) => {
                                setEditSubmission(submission)} } />
            }
            else if(surveyAdditionalInfo && uuidInvestigation){
                return <FillSurvey initData={editSubmission} idSubmission={editSubmission ? editSubmission.id : undefined} uuid={surveyAdditionalInfo.uuid} sections={surveyAdditionalInfo.sections} 
                            country={surveyAdditionalInfo.country} uuidInvestigation={uuidInvestigation}
                            uuidPatient={uuidPatient!} 
                            callBackDataCollectionSavedWithData = {async (data) => {
                                console.log("Data Saved", data);
                                const amountField = data.surveyRecords.find((record:any) => record.surveyField.name.toLocaleLowerCase() === "amount");
                                const amount = amountField ? amountField.value : 0;
    
                                const prevBillItemIndex = items.findIndex((item:BillItem) => item.additionalInfoId === data.id);
    
                                if(prevBillItemIndex !== -1){
                                    const tempItems = [...items];
                                    tempItems[prevBillItemIndex].amount = amount;
                                    tempItems[prevBillItemIndex].updatedAt = new Date();
                                    await dispatch(saveBillingItems(tempItems));
                                }
                                else{
                                    const nextOrder = getNextOrderNumber(items);
                                    const additionalItem:BillItem = {
                                        concept: translate(`hospital.billing.item.additional_info`).toString(),
                                        type: TYPE_BILL_ITEM.DISCOUNT_ADDITIONAL_INFO,
                                        quantity:1,
                                        amount: amount,
                                        unitCost: amount,
                                        additionalInfoId: data.id,
                                        updatedAt: new Date(),
                                        order: nextOrder
                                    }
                                    await dispatch(pushBillingItems([additionalItem]))
                                }
                                
                                setCurrentItem(DEFAULT_CURRENT_ITEM);
                                setShowModal({show:false, type:""});
                                setEditSubmission(undefined);
                            }
                            }/>
                
            }
        }
        
    }
    function onCloseModal(){
        setShowModal({show:false, type:""});
        updateStates(BillItemKeys.type, "", "");
    }

    function renderInsertedBillItems() {
        let filteredItems = [...items];
        if(print){
            filteredItems = items.filter((item) =>{
                return ![TYPE_BILL_ITEM.DISCOUNT_ADDITIONAL_INFO, TYPE_BILL_ITEM.HIDDEN_VALUE].includes(item.type)
            } );
        }
        let rows: BillItemTable[] = filteredItems.map((val:BillItemTable, index:number) => {

            if(val.type === TYPE_BILL_ITEM.DISCOUNT_ADDITIONAL_INFO && print){
                return; 
            }
            let color = TYPES_DISCOUNT.includes(parseInt(val.type)) ? red[900] : parseInt(val.type) === TYPE_BILL_ITEM.DISCOUNT_ADDITIONAL_INFO ? blue[900] : "black";
             
            
            let rowElement:{[id: string] : JSX.Element} = {}
            filteredColumns.forEach((col:Column) => {
                if(col.type === "amount"){
                    // Check if this is a custom amount field (not the standard 'amount' field)
                    // Use string comparison since TypeScript doesn't know about custom fields
                    const colNameStr = col.name as string;
                    let amountValue: number;
                    if (colNameStr === 'patientPaymentAmount') {
                        // Calculate from insuranceClaim if available, otherwise use stored value
                        if ((val as any).insuranceClaim) {
                            amountValue = (val as any).insuranceClaim.patientTotalBill - (val as any).insuranceClaim.approvedAmount;
                        } else {
                            amountValue = (val as any)[colNameStr] || 0;
                        }
                    } else if (colNameStr === 'total') {
                        // Get total from insuranceClaim
                        amountValue = (val as any).insuranceClaim?.patientTotalBill || 0;
                    } else {
                        // Use standard getAmount function for regular amount field
                        const amount = (TYPES_DISCOUNT.includes(Number(val.type))) ? parseInt(getAmount(val)) * -1 : parseInt(getAmount(val));
                        amountValue = amount;
                    }
                    
                    let amountString:string = new Intl.NumberFormat(activeLanguage.code).format(amountValue);
                    const percentSymbol = (Number(val.type) === TYPE_BILL_ITEM.DISCOUNT_PERCENT ? "%" : "");
                    amountString =  `${amountString} ${percentSymbol} `;

                    if(colNameStr === 'unitCost' && canUpdateBill && canEditPharmacyPrices && Number(val.type) === TYPE_BILL_ITEM.PHARMACY_ITEM){
                        rowElement[col.name] = <TextField type="number" size="small" value={amountValue}
                            onChange={(e) => { const newVal = parseFloat(e.target.value); if(!isNaN(newVal) && newVal > 0){ updateUnitCostBillItem(index, newVal); } }}
                            inputProps={{ min: 1, step: 1 }} style={{ width: '100px' }} />;
                    } else if(colNameStr === 'unitCost' && isPriceEditable(val)){
                        // SHER: edit-price button → inline editable input; commit on Enter/blur.
                        rowElement[col.name] = editingPriceIndex === index
                            ? <TextField type="number" size="small" autoFocus value={editingPriceValue}
                                onChange={(e) => setEditingPriceValue(e.target.value)}
                                onBlur={() => commitPrice(index)}
                                onKeyDown={(e) => { if(e.key === 'Enter'){ commitPrice(index); } else if(e.key === 'Escape'){ setEditingPriceIndex(null); } }}
                                inputProps={{ min: 1, step: 1 }} style={{ width: '110px' }} />
                            : <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                <Typography variant="body2" style={{ color: color }}>{amountString}</Typography>
                                <IconButton size="small" data-testid={`edit-price-${index}`}
                                    aria-label={translate('hospital.billing.item.edit_price').toString()}
                                    onClick={() => { setEditingPriceIndex(index); setEditingPriceValue(String(amountValue)); }}>
                                    <EditIcon fontSize="small" />
                                </IconButton>
                              </span>;
                    } else {
                        rowElement[col.name] =  <Typography variant="body2" style={{ color: color }}>{amountString}</Typography>
                    }
                }
                else if(col.type === "subtotal"){
                    let subtotal = parseInt(getAmount(val) as string) * parseInt(val.quantity);
                    if(Number(val.type) === TYPE_BILL_ITEM.DISCOUNT_PERCENT){             
                        //Calculate the percetage of the subtotal
                        subtotal = (calculateTotalBill(items, [TYPE_BILL_ITEM.DISCOUNT_PERCENT, TYPE_BILL_ITEM.DISCOUNT_ADDITIONAL_INFO, TYPE_BILL_ITEM.HIDDEN_VALUE]) * parseInt(getAmount(val))) / 100;

                        const amountString:string = new Intl.NumberFormat(activeLanguage.code).format(subtotal) + "%";
                        rowElement[col.name] =  <Typography variant="body2" style={{ color: color }}>{amountString}</Typography>   
                    }
                    subtotal = (TYPES_DISCOUNT.includes(Number(val.type))) ? subtotal * -1 : subtotal;
                    
                    let amountString:string = new Intl.NumberFormat(activeLanguage.code).format(subtotal);
                    rowElement[col.name] =  <Typography variant="body2" style={{ color: color }}>{amountString}</Typography>   
                }
                else if(col.type === "readonly_amount"){
                    const readonlyValue = Number((val as any)[col.name]) || 0;
                    const readonlyString = new Intl.NumberFormat(activeLanguage.code).format(readonlyValue);
                    rowElement[col.name] = <Typography variant="body2" style={{ color: color }}>{readonlyString}</Typography>
                }
                else if(col.type === "type"){
                    const typeSelected = getBillItemTypeKey(val.type);
                    // SHER-546: an unusable type renders as nothing rather than the raw value —
                    // this cell also goes onto printed bills and invoices, where "undefined" or a
                    // bare "10" would be worse than an empty cell.
                    rowElement[col.name] = <Typography variant="body2" style={{ color: color }}>{typeSelected ? <Translate id={`hospital.billing.item.types.${typeSelected.toLocaleLowerCase()}`} /> : null}</Typography>
                }
                else if(col.type === "number" ){
                    if(!canUpdateBill ||  val.type === TYPE_BILL_ITEM.DISCOUNT_ADDITIONAL_INFO || Number(val.type) === TYPE_BILL_ITEM.AUTO_ANAESTHESIA){
                        rowElement[col.name] =  <Typography variant="body2" style={{ color: color }}>{val[col.name]}</Typography>
                    }
                    else{
                        rowElement[col.name] = <QuantitySelector quantity={val[col.name] as number} onQuantityChange={(q) => updateQuantityBillItem(index, q)} />
                    }
                }
                else if(col.type === "date"){
                    // Check if this is a custom date field (not the standard date fields)
                    const colNameStr = col.name as string;
                    let date: Date;
                    if (colNameStr === 'patientBillCreatedAt') {
                        // Access custom property directly for insurance claim field
                        date = (val as any)[colNameStr];
                        
                    } else {
                        // Use standard date handling for regular date fields
                        date = getDateFromStringOrDate(val[col.name]);
                    }
                    rowElement[col.name] = <Typography variant="body2" style={{ color: color }}>{ dateToFullDateString(date, activeLanguage.code) }</Typography>
                }
                else{
                    let conceptText = val[col.name] as string;
                    
                    const plainName = <Typography variant="body2" style={{ color: color }}>{conceptText}</Typography>
                    let nameItem = plainName;
                    
                    // Handle patient bill ID as clickable link
                    const colNameStr = col.name as string;
                    if (colNameStr === 'patientBillId') {
                        const billId = (val as any).patientBillId;
                        const billUuid = (val as any).patientBillUuid;
                        
                        if (billId && billUuid) {
                            nameItem = (
                                <Button
                                    size="small"
                                    variant="text"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        history.push(HOSPITAL_BILLING_VIEW_DOCUMENT.replace(":uuidDocument", billUuid));
                                    }}
                                    sx={{ 
                                        minWidth: 'auto',
                                        padding: '2px 8px',
                                        textTransform: 'none',
                                        color: 'primary.main',
                                        '&:hover': {
                                            textDecoration: 'underline',
                                            backgroundColor: 'transparent'
                                        }
                                    }}
                                >
                                    {billId}
                                </Button>
                            );
                        } else {
                            nameItem = <Typography variant="body2" style={{ color: color }}>-</Typography>;
                        }
                    }
                    else if(val.type === TYPE_BILL_ITEM.DISCOUNT_ADDITIONAL_INFO){
                        nameItem = (
                        <div style={{display:'flex', flexDirection:'row', justifyContent:'space-between'}}>
                            {plainName}
                            <IconButton
                                onClick={(e) => {
                                    setShowModal({show:true, type:"additional_info"});
                                    setShowAdditionalInfoID(val.additionalInfoId) 
                                    e.stopPropagation();
                                    
                                }}
                                size="large">
                                <IconGenerator type="view" />
                            </IconButton> 
                        </div>)
                    }
                    rowElement[col.name] = nameItem
                }
            })
            return {...rowElement, id: index, used: (!hidePatientPaymentUi && !TYPES_DISCOUNT.includes(val.type) && Number(val.type) !== TYPE_BILL_ITEM.AUTO_ANAESTHESIA && uuidPatient) ? renderCheckOrDate("primary", items[index].used, index, usedItem) : <React.Fragment></React.Fragment>,
                    paid: (!hidePatientPaymentUi && !TYPES_DISCOUNT.includes(val.type) && Number(val.type) !== TYPE_BILL_ITEM.AUTO_ANAESTHESIA && bill && bill.type === DocumentType.INVOICE) ? renderCheckOrDate("secondary", items[index].paid, index, paidItem) : <React.Fragment></React.Fragment>,
                    delete: Number(val.type) === TYPE_BILL_ITEM.AUTO_ANAESTHESIA ? <React.Fragment></React.Fragment> : <IconButton onClick={() => removeItem(index)} size="large">
                        <IconGenerator type="delete" />
                    </IconButton>};
    
        }) as BillItemTable[];

        return rows;
    }

    function renderRowAddItem(){
        let row:BillItemTable = {} as BillItemTable;
        filteredColumns.forEach((col:Column) => {
            let value;
            switch(col.type){
                case "autocomplete":
                    // SHER-460: in free-item-only mode the catalogue lives in the PrestationSelector
                    // add bar, so the "+" row offers no billables — only the typed free item.
                    const billableOptions: BillableOption[] = addRowFreeItemOnly ? [] : billables ? billables.filter((billable) => {
                        // SHER-342 (M2): PERCENT_CHARGE billables are derivation templates
                        // (e.g. "Anesthésie 50%"), not selectable lines — picking one would
                        // persist a nonsense fixed-amount line. They reach the bill only as
                        // the auto-derived "[act] - Anesthésie" line via relatedBillables.
                        if(Number(billable.type) === TYPE_BILL_ITEM.PERCENT_CHARGE){
                            return false;
                        }
                        if(!repeatBillItems){
                            const itemSelected = items.find((item) => item.id === billable.id);
                            return !itemSelected;
                        }
                        else{
                            return billable
                        }
                    }).sort((a,b) => a.concept > b.concept ? 1 : -1 ) as BillableOption[] : [];
                    // SHER-460: a free item is in progress while the concept is typed but no billable
                    // was picked — that's when the cashier needs the curated Type selector below.
                    // SHER-546: show the curated selector exactly when the bill has no Type column
                    // of its own, which is the real condition `!withDiscount` stood in for.
                    const freeItemInProgress = !hasTypeColumn && isFreeItemInProgress(currentItem);
                    value = billables && billables.length > 0 ?
                    <React.Fragment>
                    <Autocomplete
                        key={`add-bill-autocomplete-${addAutocompleteKey}`}
                        freeSolo
                        openOnFocus
                        PopperComponent={WideBillablePopper}
                        options={billableOptions}
                        // SHER-460: category pills narrow the options; the freeSolo "élément libre"
                        // row is appended when the typed text matches no catalogue billable.
                        filterOptions={(options, params) => {
                            const byCategory = filterBillablesByCategory(options as BillableOption[], activeCategory) as BillableOption[];
                            const filtered = billableFilterOptions(byCategory, params);
                            const trimmed = params.inputValue.trim();
                            // In free-item-only mode everything typed IS a free item, so the
                            // "no match — add as free item" row is redundant noise (the curated
                            // Type selector already appears as you type). Only offer it when the
                            // catalogue is present and the typed text matches nothing.
                            if(!addRowFreeItemOnly && trimmed !== ""){
                                const exists = (options as BillableOption[]).some((option) => (option.concept ?? "").toLowerCase() === trimmed.toLowerCase());
                                if(!exists){
                                    filtered.push({
                                        inputValue: trimmed,
                                        isFreeItemOption: true,
                                        concept: translate("hospital.billing.item.add_free_item").toString().replace("%X", trimmed),
                                    } as BillableOption);
                                }
                            }
                            return filtered;
                        }}
                        renderOption={(props, option) => {
                            const billableOption = option as BillableOption;
                            if(billableOption.isFreeItemOption){
                                return <li {...props} style={{ backgroundColor: "#fff8e1", fontStyle: "italic", borderTop: "1px solid #eee" }}>{billableOption.concept}</li>
                            }
                            const optionCategory = typeof billableOption.category === "string" ? billableOption.category.trim() : "";
                            return <li {...props} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px" }}>
                                <span>{billableOption.concept}</span>
                                {optionCategory && <ColourChip size="small" label={optionCategory} rgbcolor={categoryColorMap.get(optionCategory) ?? "#616161"} />}
                            </li>
                        }}
                        PaperComponent={({ children, ...paperProps }) => (
                            <Paper {...paperProps}>
                                {!addRowFreeItemOnly && billableCategories.length > 0 &&
                                    <div data-testid="billable-category-pills" onMouseDown={(event) => event.preventDefault()}
                                        style={{ display: "flex", flexWrap: "wrap", gap: "4px", padding: "8px", borderBottom: "1px solid #eee" }}>
                                        {/* ColourChip's `active` prop DIMS the chip (opacity 0.5), so we pass active when a pill
                                            is NOT the selected one — the active filter stays fully opaque, the rest read as muted. */}
                                        <ColourChip label={translate("hospital.billing.item.category_all").toString()}
                                            rgbcolor="#616161" active={activeCategory !== null}
                                            onClick={() => setActiveCategory(null)} />
                                        {billableCategories.map((category, index) => (
                                            <ColourChip key={category} label={category}
                                                rgbcolor={categoryColorMap.get(category) ?? categoryPillColor(index)} active={activeCategory !== category}
                                                onClick={() => setActiveCategory(category)} />
                                        ))}
                                    </div>
                                }
                                {children}
                            </Paper>
                        )}
                        onChange={(event, newValue) => {
                            if (typeof newValue === 'string') {
                                onBillableSelected(newValue);
                            } else if (newValue && newValue.inputValue) {
                                // Create a new value from the user input (free item — no billableId)
                                onBillableSelected(newValue.inputValue);
                            } else if(newValue){
                                onBillableSelected(newValue?.concept, newValue.id, rows.length - 1,);
                            }
                            }}

                            getOptionLabel={(option) => {
                            if (typeof option === 'string') {
                                return option;
                            }
                            if (option?.inputValue) {
                                return String(option.inputValue);
                            }
                            const label = option?.concept ?? (option as { name?: string }).name;
                            return typeof label === 'string' ? label : String(label ?? '');
                            }}
                        style={{ width: '100%', minWidth: 360 }}
                        renderInput={(params:any) =>
                            <TextField {...params} fullWidth inputRef={focusConceptWithoutScroll} label="Concept" error={fieldErrors.concept !== ""}
                                helperText={helperText(BillItemKeys.concept)} color="secondary"
                                onChange={(event:React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => changeField(event.target.value, BillItemKeys.concept)}
                                variant="outlined" />
                            }
                    />
                    {freeItemInProgress &&
                        <FormControl variant="outlined" fullWidth margin="dense" error={Boolean(fieldErrors.type)}>
                            <InputLabel id="free_item_type">{translate("hospital.billing.item.type").toString()}</InputLabel>
                            <Select labelId="free_item_type" id="free_item_type" label="type"
                                value={currentItem[BillItemKeys.type] ?? ""}
                                onChange={(event:any) => changeFreeItemType(event.target.value)}>
                                {FREE_ITEM_TYPE_OPTIONS.map(({ key, value: typeValue }) => (
                                    <MenuItem key={key} value={typeValue}><Translate id={`hospital.billing.item.types.${key}`} /></MenuItem>
                                ))}
                            </Select>
                            {fieldErrors.type ? <FormHelperText>{helperText(BillItemKeys.type)}</FormHelperText> : null}
                        </FormControl>
                    }
                    </React.Fragment> : <TextField label="Concept" error={fieldErrors.concept !== ""}
                            helperText={helperText(BillItemKeys.concept)} color="secondary"
                            onChange={(event:React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => changeField(event.target.value, BillItemKeys.concept)}
                            variant="outlined" />
                break;
                case "type":
                    // SHER-546: the false arm is unreachable (a `type` column only survives the
                    // filter above when the bill has one), but key it on the same condition as
                    // the rest so no reader reasons from the proxy again.
                    value = hasTypeColumn ?
                    <FormControl variant="outlined" fullWidth margin="dense" error={fieldErrors.type !== ""}>
                        <InputLabel id="show_treatment">Type</InputLabel>
                        <Select
                            labelId="show_treatment"
                            id="show_treatment"
                            label="type"
                            value={currentItem[BillItemKeys.type]}
                            onChange={(event:React.ChangeEvent<{
                                name?: string | undefined;
                                value: unknown;
                            }>) => changeType(event, BillItemKeys.type)}
                            >
                                {
                                    orderBillItemTypeOptionsByLabel(
                                        TYPES_BILL_ITEM.filter(([key, value]) => {
                                            if(value === TYPE_BILL_ITEM.DISCOUNT_ADDITIONAL_INFO){
                                                return canUseAdditionalInfo && surveyAdditionalInfo;
                                            }
                                            return true;
                                        }),
                                        translate,
                                    ).map(([key, value]) =>{
                                        return <MenuItem value={value}><Translate id={`hospital.billing.item.types.${key.toLowerCase()}`} /></MenuItem>
                                    })
                                }
                            
                        </Select>
                        {/* SHER-546: this path can now raise type_not_selected, so it needs the same
                            reason line the curated selector already had — a red box with no message
                            leaves the cashier with nothing to act on. */}
                        {fieldErrors.type ? <FormHelperText>{helperText(BillItemKeys.type)}</FormHelperText> : null}
                    </FormControl> : undefined
                break;
                case "amount":
                    value = <TextField label={<Translate id="hospital.billing.item.unitCost" />} variant="outlined" 
                        helperText={helperText(BillItemKeys.amount)} 
                        color="secondary"
                        error={fieldErrors.amount !== ""} type="text" 
                        onChange={(event:React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => changeField(event.target.value, BillItemKeys.amount)} />   
                    break;
                case "readonly_amount":
                    value = <React.Fragment></React.Fragment>;
                    break;
                case "number":
                    value = <TextField label={<Translate id={`hospital.billing.item.${col.name}`} />} variant="outlined"
                        helperText={helperText(col.name as BillItemKeys)}
                        color="secondary"
                        error={fieldErrors[col.name] !== ""} type="text"
                        onChange={(event:React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => changeField(event.target.value, col.name as BillItemKeys)} />
                    break;
           }
           // @ts-ignore: Unreachable code error
           row[col.name] = value;
        });

        row = {...row, id: rows.length - 1, delete: <ButtonAdd  onClick={() => addCurrentItem()}></ButtonAdd >}
        return row;
    }

    async function addCurrentItem(keepOpen: boolean = false) {
        let tempFieldErrors = { ...fieldErrors };
        //Fuerzo el valor 0 para diferenciarlo del valor por defecto.
        //currentItem["type"] = 0;
        filteredColumns.forEach((col) => {

            const key = col.name.toString();
            const rawValue = currentItem[key as keyof BillItem];
            const value = rawValue === undefined || rawValue === null ? '' : String(rawValue);
            tempFieldErrors[col.name] = validateKeyItem(value, col.name);
        })
        if (currentItem[BillItemKeys.type] === 2 &&  parseInt(currentItem[BillItemKeys.amount].toString()) > 99) {
            tempFieldErrors[BillItemKeys.amount] = "amount_percent";
        }
        // SHER-460/546: a free item has no billable to inherit a type from, so one must be
        // chosen — on every bill, keyed on whether a Type control is actually rendered rather
        // than on `withDiscount`, which is only a proxy for it. Only the accepted set differs.
        // Why: docs/features/billing/decisions/SHER-546-free-line-type-required.md
        const freeItemNeedsType = isFreeItemInProgress(currentItem);
        const freeItemTypeIsValid = hasTypeColumn
            ? isBillItemType(currentItem[BillItemKeys.type])
            : isValidFreeItemType(currentItem[BillItemKeys.type]);
        if (freeItemNeedsType && !freeItemTypeIsValid) {
            tempFieldErrors[BillItemKeys.type] = "type_not_selected";
        }
        if (Object.values(tempFieldErrors).reduce((acc, val) => acc && (val === ""), true)) {
            let tempItems = [...items];
            let nextOrder = getNextOrderNumber(tempItems);
            
            // SHER-342: if this act links a PERCENT_CHARGE template, stamp it (concept + percent)
            // so reconcileAnaesthesiaItems can derive the "[act] - {template}" line. The template
            // is config, not a billable line, so it is NOT auto-added at a fixed price below.
            if(currentItem.relatedBillables && currentItem.relatedBillables.length > 0){
                const template = currentItem.relatedBillables
                    .map((id) => billables.find((b) => b.id === id))
                    .find((b) => b && b.type === TYPE_BILL_ITEM.PERCENT_CHARGE);
                if(template){
                    currentItem.percentTemplate = { concept: template.concept, percent: Number(template.amount) };
                }
            }

            // Add the main item with order
            currentItem.updatedAt = new Date();
            currentItem.order = nextOrder++;
            tempItems.push({...currentItem});

            // Add related billables with consecutive order numbers (PERCENT_CHARGE templates
            // are skipped — they drive the derived line via percentTemplate, not a fixed line).
            if(currentItem.relatedBillables && currentItem.relatedBillables.length > 0){
                for(let i = 0; i < currentItem.relatedBillables.length; i++){
                    const billable:Billable | undefined = billables.find((billable) => billable.id === currentItem?.relatedBillables[i]);
                    if(billable && billable.type !== TYPE_BILL_ITEM.PERCENT_CHARGE){
                        const currentBillItem:BillItem = {...billable, quantity:1, unitCost: billable.amount, updatedAt: new Date(), order: nextOrder++}
                        delete currentBillItem.id;
                        tempItems.push(currentBillItem);
                    }
                }
            }

            await dispatch(saveBillingItems(tempItems))
            // SHER-460: after a catalogue pick (keepOpen) leave the add-row open and remount the
            // autocomplete (clears its input + reopens on focus) so the next item can be chained.
            // The manual "add" button path (free items, etc.) collapses the row as before.
            if (keepOpen) {
                setAddAutocompleteKey((key) => key + 1);
            } else {
                setAddingItem(false);
            }
            setCurrentItem(DEFAULT_CURRENT_ITEM);
        }
        setErrorBill(undefined);
        setFieldErrors(tempFieldErrors);
    }
    async function removeItem(index: number) {
        console.log("Borrar " + index);
        const tempItems = [...items];
        const elementToDelete = {...tempItems[index]};
        if(elementToDelete.additionalInfoId){
            setEditSubmission(undefined);
            setShowAdditionalInfoID(-1);
            await dispatch(removeSubmissionPatient(uuidPatient ,elementToDelete.additionalInfoId, surveyAdditionalInfo.uuid));
        }
        tempItems.splice(index, 1);
        await dispatch(saveBillingItems(tempItems));
        setErrorBill(undefined);
    }
    
    const rows = renderInsertedBillItems();

    if (addingItem) {
        const rowAddItem = renderRowAddItem();
        rows.push(rowAddItem)
    }
    else if (canUpdateBill && !print && !hideAddItemFooter) {
        // @ts-ignore: Unreachable code error
        let field: BillItemTable = {}
        filteredColumns.forEach((col:Column) => {
            let value = <React.Fragment></React.Fragment>

        // @ts-ignore: Unreachable code error
           field[col.name] = value;
        });
        field = {...field, delete: <React.Fragment><Translate id="hospital.billing.item.add_item" /> <ButtonAdd disabled={addingItem}
                        type="button" data-testid="add_bill"
                        onClick={() => {
                            setErrorBill(undefined);
                            setAddingItem(true)
                        }} /></React.Fragment>}

        rows.push(field);
    }
    
    if (items.length > 0 && showTotal) {
        rows.push({
            id: items.length, 
            colSpan:"amount",
            concept: <Typography style={{fontWeight:'bold', fontSize:'1.2rem'}} ><Translate id={`hospital.billing.bill.total`} /></Typography>,
            type : <></>, 
            amount: <Typography style={{ fontWeight: 'bold', minWidth:'2rem' }} >{new Intl.NumberFormat(activeLanguage.code).format(totalAmountBill) + " " + currency}</Typography>,
            delete: <React.Fragment></React.Fragment>,
        });
        
        
    }
    if(!canUpdateBill && print){        
        const hasAdditionalInfo = items.find((item) =>{
            return item.type === TYPE_BILL_ITEM.DISCOUNT_ADDITIONAL_INFO
        });
        if(hasAdditionalInfo){
            rows.push({
                id: items.length, 
                colSpan:"amount",
                concept: <Typography style={{fontSize:'1rem'}} ><Translate id="hospital.billing.bill.insurance_coverage" /></Typography>,
                type : <></>, 
                amount: <Typography style={{  fontSize:'1rem' }} >{new Intl.NumberFormat(activeLanguage.code).format(hasAdditionalInfo.amount) + " " + currency}</Typography>,
                delete: <React.Fragment></React.Fragment>,
            });
            rows.push({
                id: items.length, 
                colSpan:"amount",
                concept: <Typography style={{fontSize:'1rem'}} ><Translate id="hospital.billing.bill.patient_responsability" /></Typography>,
                type : <></>, 
                amount: <Typography style={{  minWidth:'2rem', fontSize:'1rem' }} >{new Intl.NumberFormat(activeLanguage.code).format(totalAmountBill - hasAdditionalInfo.amount) + " " + currency}</Typography>,
                delete: <React.Fragment></React.Fragment>,
            });
            rows.push({
                id: items.length, 
                colSpan:"amount",
                concept: <Typography style={{fontWeight:'bold', }}><Translate id="hospital.billing.bill.remaining_amount" /></Typography>,
                type : <></>, 
                amount: <Typography style={{  minWidth:'2rem' }} >{new Intl.NumberFormat(activeLanguage.code).format(totalAmountBill - hasAdditionalInfo.amount) + " " + currency}</Typography>,
                delete: <React.Fragment></React.Fragment>,
            });
            
            
        }
    }
    // let  headCells = [{ id: "concept", alignment: "left", label: <Translate id={`hospital.billing.item.concept`} /> },
    // { id: "amount", alignment: "left", label: <Translate id={`hospital.billing.item.amount`} /> }
    // ]
    let  headCells = filteredColumns.map((col) => {
        // Use custom translationKey if provided, otherwise fall back to default pattern
        const translationKey = col.translationKey || `hospital.billing.item.${col.name}`;
        return { id: col.name, alignment: "left", label: <Translate id={translationKey} />}
    })

    // if(withDiscount){
    //     headCells.splice(1, 0, { id: "type", alignment: "left", label: <Translate id={`hospital.billing.item.type`} /> });
    // }

    if (canUpdateBill) {
        // @ts-ignore: Unreachable code error
        headCells.push({ id: "delete", alignment: "right", label: <React.Fragment></React.Fragment> });
    }
    else if(bill?.type === DocumentType.INVOICE && bill.status === DocumentStatus.CLOSED) {
        //Remove  headcell with name updatedAt
        headCells = headCells.filter((cell) => cell.id !== "updatedAt");
        if(!print && !hidePatientPaymentUi){
            headCells.push({ id: "paid", alignment: "right",  markAllCallback: (async () => await toggleAllItemsAs("paid")) , label: <React.Fragment><Translate id="general.paid" /></React.Fragment> });
            // Only show "used" column for regular bills (not insurance bills)
            // Insurance bills have uuidPatient as undefined
            if(uuidPatient){
            headCells.push({ id: "used", alignment: "right",  markAllCallback: (async () => await toggleAllItemsAs("used")) , label: <React.Fragment><Translate id="general.used" /></React.Fragment> });
            }
        }
    }
   
    return (
        <div style={{width:'100%'}} ref={printRef} >
            <Modal key="modal" fullWidth medium open={showModal.show} 
                title={<Translate id={`hospital.billing.modal.title_${showModal.type}`} />} closeModal={() => onCloseModal()}>
            {
                renderContentModal()
            }
            </Modal>
        
            <Grid container item>
                <Grid item xs={12}>
                    <EnhancedTable noFooter noHeader noPagination dense={print}
                        noSelectable disableOrder headCells={headCells} rows={rows} />
                </Grid>
                <Grid item xs={12} style={{ display: "flex", flexDirection: "column" }} >
                    {
                        errorBill &&
                        <Grid item xs={12} style={{ textAlign: "right", paddingTop: "10px" }}>
                            <Typography variant="body2" style={{ color: "red" }} >{errorBill}</Typography>
                        </Grid>
                    }
                   {
                    !hidePatientPaymentUi && (!bill || !isBillCancelled(bill.status)) &&

                    <GridBottom hide={print} item xs={12} paddingTop={5} >
                        {bill && bill.uuid && bill.status !== DocumentStatus.CLOSED &&
                            <Button
                                variant="outlined"
                                startIcon={<RefreshIcon />}
                                disabled={refreshingPrices}
                                onClick={onRefreshPrices}
                                style={{ marginRight: '1rem' }}
                                data-testid="refresh-prices"
                            >
                                {refreshingPrices
                                    ? <Translate id="hospital.billing.refreshing_prices" />
                                    : refreshResult?.show
                                        ? refreshResult.count >= 0
                                            ? `${refreshResult.count} ${translate("hospital.billing.prices_updated")}`
                                            : "Error"
                                        : <Translate id="hospital.billing.refresh_prices" />
                                }
                            </Button>
                        }
                        <ButtonOk disabled={items.length === 0} onClick={() => onClickContinue(items)}
                            data-testid="continue-modal">
                            {renderTextOkButton()}
                        </ButtonOk>
                    </GridBottom>
                   }
                    

                </Grid>
            </Grid>
        </div>
    )
}

export const BillItems = withLocalize(BillItemsCore)
